import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeScanner } from 'html5-qrcode';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  QrCode,
  Camera,
  X,
  MapPin,
  Factory,
  ClipboardList,
  ChevronRight,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Search,
  List,
  RefreshCw,
  SwitchCamera
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE } from '../utils/apiClient';

const API = API_BASE;

const QRScanner = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  // Refs
  const html5QrCodeRef = useRef(null);
  const isMountedRef = useRef(true);
  const scannerContainerId = 'qr-reader-container';

  // State
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  
  // Manual selection mode
  const [manualMode, setManualMode] = useState(false);
  const [zones, setZones] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [loadingZones, setLoadingZones] = useState(false);
  
  // Camera selection
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    fetchSitesAndZones();
    
    return () => {
      isMountedRef.current = false;
      cleanupScanner();
    };
  }, []);

  const cleanupScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING state
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.log('Scanner cleanup:', err.message);
      } finally {
        html5QrCodeRef.current = null;
      }
    }
  };

  const fetchSitesAndZones = async () => {
    try {
      const [sitesRes, zonesRes] = await Promise.all([
        axios.get(`${API}/sites`, { headers: getAuthHeaders() }),
        axios.get(`${API}/zones`, { headers: getAuthHeaders() })
      ]);
      if (isMountedRef.current) {
        setSites(sitesRes.data);
        setZones(zonesRes.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const getCameras = async () => {
    try {
      const devices = await Html5Qrcode.getCameras();
      if (devices && devices.length > 0) {
        setCameras(devices);
        // Prefer back camera
        const backCamera = devices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('arrière') ||
          d.label.toLowerCase().includes('environment')
        );
        setSelectedCamera(backCamera?.id || devices[0].id);
        return devices;
      }
      return [];
    } catch (err) {
      console.error('Error getting cameras:', err);
      return [];
    }
  };

  const startScanning = async () => {
    setError(null);
    setScannedData(null);
    setCameraError(null);
    setCameraReady(false);

    // Check if we're on HTTPS or localhost
    const isSecure = window.location.protocol === 'https:' || 
                     window.location.hostname === 'localhost' ||
                     window.location.hostname === '127.0.0.1';
    
    if (!isSecure) {
      setCameraError("L'accès à la caméra nécessite une connexion sécurisée (HTTPS). Utilisez la sélection manuelle.");
      setManualMode(true);
      return;
    }

    try {
      // Get available cameras first
      const availableCameras = await getCameras();
      
      if (availableCameras.length === 0) {
        throw new Error('NotFoundError');
      }

      // Cleanup any existing scanner
      await cleanupScanner();

      // Small delay to ensure DOM is ready
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!isMountedRef.current) return;

      // Create new scanner instance
      html5QrCodeRef.current = new Html5Qrcode(scannerContainerId);
      
      const cameraId = selectedCamera || availableCameras[0].id;
      
      await html5QrCodeRef.current.start(
        cameraId,
        {
          fps: 15,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
          disableFlip: false
        },
        onScanSuccess,
        (errorMessage) => {
          // Silent - QR not detected yet
        }
      );
      
      if (isMountedRef.current) {
        setScanning(true);
        setCameraReady(true);
      }
    } catch (err) {
      console.error("Error starting scanner:", err);
      
      if (!isMountedRef.current) return;
      
      let errorMessage = "Impossible d'accéder à la caméra.";
      
      if (err.name === 'NotAllowedError' || err.message?.includes('NotAllowedError')) {
        errorMessage = "Permission caméra refusée. Veuillez autoriser l'accès à la caméra dans les paramètres de votre navigateur, puis actualisez la page.";
      } else if (err.name === 'NotFoundError' || err.message?.includes('NotFoundError')) {
        errorMessage = "Aucune caméra détectée sur cet appareil. Utilisez la sélection manuelle.";
      } else if (err.name === 'NotReadableError' || err.message?.includes('NotReadableError')) {
        errorMessage = "La caméra est déjà utilisée par une autre application. Fermez les autres applications utilisant la caméra.";
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = "Configuration caméra non supportée. Essayez avec une autre caméra.";
      } else if (err.message?.includes('No MultiFormat Readers')) {
        errorMessage = "Erreur d'initialisation du scanner. Réessayez.";
      }
      
      setCameraError(errorMessage);
      setManualMode(true);
      toast.error("Scanner non disponible");
    }
  };

  const stopScanning = useCallback(async () => {
    await cleanupScanner();
    if (isMountedRef.current) {
      setScanning(false);
      setCameraReady(false);
    }
  }, []);

  const onScanSuccess = useCallback(async (decodedText) => {
    // Prevent multiple scans
    if (!isMountedRef.current) return;
    
    await stopScanning();

    // Parse QR code data - format: gembacoach://zone/{zone_id}
    const match = decodedText.match(/gembacoach:\/\/zone\/(.+)/);
    
    if (!match) {
      // Try alternative format (just zone ID)
      const uuidMatch = decodedText.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
      if (uuidMatch) {
        await fetchZoneData(decodedText);
        return;
      }
      
      setError("QR code non reconnu. Assurez-vous de scanner un QR code GembaCoach valide.");
      toast.error("QR code invalide");
      return;
    }

    const zoneId = match[1];
    await fetchZoneData(zoneId);
  }, [stopScanning]);

  const fetchZoneData = async (zoneId) => {
    if (!isMountedRef.current) return;
    
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/scan/zone/${zoneId}`, {
        headers: getAuthHeaders()
      });
      
      if (isMountedRef.current) {
        setScannedData(response.data);
        toast.success("Zone identifiée !");
      }
    } catch (err) {
      console.error("Error fetching zone:", err);
      if (isMountedRef.current) {
        if (err.response?.status === 404) {
          setError("Zone non trouvée. Le QR code est peut-être obsolète ou invalide.");
        } else {
          setError("Erreur lors de la récupération des données. Vérifiez votre connexion.");
        }
        toast.error("Zone non trouvée");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleManualSelection = async () => {
    if (!selectedZone) {
      toast.error("Veuillez sélectionner une zone");
      return;
    }
    await fetchZoneData(selectedZone);
  };

  const startTourWithChecklist = (checklist) => {
    navigate('/gemba', {
      state: {
        preselectedSite: scannedData.site,
        preselectedZone: scannedData.zone,
        preselectedChecklist: checklist
      }
    });
  };

  const resetScanner = async () => {
    await stopScanning();
    setScannedData(null);
    setError(null);
    setCameraError(null);
    setSelectedZone('');
    setManualMode(false);
  };

  const switchCamera = async () => {
    if (cameras.length <= 1) return;
    
    const currentIndex = cameras.findIndex(c => c.id === selectedCamera);
    const nextIndex = (currentIndex + 1) % cameras.length;
    const nextCamera = cameras[nextIndex];
    
    setSelectedCamera(nextCamera.id);
    
    // Restart with new camera
    await stopScanning();
    setTimeout(() => startScanning(), 200);
  };

  const filteredZones = selectedSite 
    ? zones.filter(z => z.site_id === selectedSite)
    : zones;

  return (
    <div className="p-4 md:p-8 space-y-6 fade-in" data-testid="qr-scanner-page">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
          <QrCode className="w-8 h-8 text-[#0E346E]" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
          SCANNER QR CODE
        </h1>
        <p className="text-slate-500 mt-2">
          Scannez le QR code d'une zone pour démarrer rapidement une tournée
        </p>
      </div>

      {/* Scanner Area */}
      {!scannedData && !error && (
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6">
            {cameraError && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-800">{cameraError}</p>
                  </div>
                </div>
              </div>
            )}

            {!scanning && !manualMode ? (
              <div className="text-center">
                <div className="w-64 h-64 mx-auto bg-slate-100 rounded-lg flex items-center justify-center mb-6 border-2 border-dashed border-slate-300">
                  <div className="text-center">
                    <Camera className="w-16 h-16 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">Appuyez pour activer</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <Button
                    onClick={startScanning}
                    className="w-full h-14 bg-[#0E346E] hover:bg-[#0A2850] text-lg font-semibold"
                    data-testid="start-scan-btn"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Activer la caméra
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setManualMode(true)}
                    className="w-full"
                    data-testid="manual-mode-btn"
                  >
                    <List className="w-4 h-4 mr-2" />
                    Sélection manuelle
                  </Button>
                </div>
              </div>
            ) : scanning ? (
              <div className="text-center">
                {/* Scanner container */}
                <div 
                  id={scannerContainerId}
                  className="w-full max-w-[300px] h-[300px] mx-auto mb-4 rounded-lg overflow-hidden bg-black"
                  style={{ border: '3px solid #0E346E' }}
                />
                
                {!cameraReady && (
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Loader2 className="w-5 h-5 animate-spin text-[#0E346E]" />
                    <span className="text-slate-500">Initialisation de la caméra...</span>
                  </div>
                )}
                
                {cameraReady && (
                  <p className="text-slate-500 mb-4 text-sm">
                    Pointez la caméra vers le QR code de la zone
                  </p>
                )}
                
                <div className="flex gap-2">
                  {cameras.length > 1 && (
                    <Button
                      variant="outline"
                      onClick={switchCamera}
                      className="flex-1"
                      data-testid="switch-camera-btn"
                    >
                      <SwitchCamera className="w-4 h-4 mr-2" />
                      Changer caméra
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={stopScanning}
                    className="flex-1"
                    data-testid="stop-scan-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Annuler
                  </Button>
                </div>
              </div>
            ) : manualMode && (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <List className="w-12 h-12 text-[#0E346E] mx-auto mb-2" />
                  <h3 className="font-semibold text-slate-900">Sélection manuelle</h3>
                  <p className="text-sm text-slate-500">Choisissez une zone dans la liste</p>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Site (optionnel)</label>
                    <Select value={selectedSite || 'all'} onValueChange={(v) => { setSelectedSite(v === 'all' ? '' : v); setSelectedZone(''); }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filtrer par site" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les sites</SelectItem>
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Zone *</label>
                    <Select value={selectedZone} onValueChange={setSelectedZone}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez une zone" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredZones.map((zone) => (
                          <SelectItem key={zone.id} value={zone.id}>
                            {zone.name} {sites.find(s => s.id === zone.site_id)?.name ? `(${sites.find(s => s.id === zone.site_id)?.name})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleManualSelection}
                    disabled={!selectedZone || loading}
                    className="w-full h-12 bg-[#0E346E] hover:bg-[#0A2850]"
                    data-testid="search-zone-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Search className="w-5 h-5 mr-2" />
                    )}
                    Rechercher
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => { setManualMode(false); setCameraError(null); }}
                    className="w-full"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Réessayer avec la caméra
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && !manualMode && (
        <Card className="max-w-md mx-auto">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#0E346E] mx-auto mb-4" />
            <p className="text-slate-600">Chargement des données de la zone...</p>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card className="max-w-md mx-auto border-red-200 bg-red-50">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-700 font-medium mb-4">{error}</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={resetScanner} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Réessayer
              </Button>
              <Button onClick={() => { setError(null); setManualMode(true); }} variant="outline">
                <List className="w-4 h-4 mr-2" />
                Mode manuel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scanned Data - Zone & Checklists */}
      {scannedData && (
        <div className="max-w-md mx-auto space-y-4 slide-up">
          {/* Success indicator */}
          <Card className="border-green-200 bg-green-50">
            <CardContent className="py-4 text-center">
              <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-green-700 font-medium">Zone identifiée avec succès</p>
            </CardContent>
          </Card>

          {/* Zone Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#0E346E]" />
                Zone sélectionnée
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Factory className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Site:</span>
                  <span className="font-medium">{scannedData.site?.name || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Zone:</span>
                  <span className="font-medium">{scannedData.zone?.name || 'N/A'}</span>
                </div>
                {scannedData.zone?.type && (
                  <Badge variant="outline" className="mt-2">
                    {scannedData.zone.type}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Available Checklists */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#B98D32]" />
                Checklists disponibles
              </CardTitle>
            </CardHeader>
            <CardContent>
              {scannedData.checklists?.length > 0 ? (
                <div className="space-y-2">
                  {scannedData.checklists.map((checklist) => (
                    <div
                      key={checklist.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors border border-transparent hover:border-slate-200"
                      onClick={() => startTourWithChecklist(checklist)}
                      data-testid={`checklist-${checklist.id}`}
                    >
                      <div>
                        <p className="font-medium text-slate-900">{checklist.name}</p>
                        <p className="text-sm text-slate-500">
                          {checklist.items?.length || 0} items • {checklist.frequency === 'quotidien' ? 'Quotidien' : 'Hebdo'}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p>Aucune checklist active pour cette zone</p>
                  <Button 
                    variant="link" 
                    onClick={() => navigate('/checklists')}
                    className="mt-2 text-[#0E346E]"
                  >
                    Créer une checklist
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={resetScanner}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Autre zone
            </Button>
            <Button 
              className="flex-1 bg-[#0E346E] hover:bg-[#0A2850]"
              onClick={() => navigate('/gemba')}
            >
              Tournée manuelle
            </Button>
          </div>
        </div>
      )}

      {/* Instructions */}
      {!scanning && !scannedData && !error && !manualMode && (
        <Card className="max-w-md mx-auto bg-slate-50 border-slate-200">
          <CardContent className="py-6">
            <h3 className="font-semibold text-slate-900 mb-3">Comment ça marche ?</h3>
            <ol className="space-y-2 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#0E346E] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                <span>Cliquez sur "Activer la caméra" ou utilisez la sélection manuelle</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#0E346E] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                <span>Scannez le QR code affiché dans la zone ou sélectionnez-la dans la liste</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#0E346E] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                <span>Choisissez la checklist et démarrez votre tournée</span>
              </li>
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default QRScanner;
